import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { useAuth } from '@/lib/auth';
import { Loader, Award, Check, Warning } from '@/components/ui/Icons';

type Attempt = {
  id: number;
  email: string | null;
  success: 0 | 1;
  reason: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
};

type Resp = { ok: true; entries: Attempt[]; page: number; per_page: number; total: number };

export default function SuperLoginAttempts() {
  const { user, loading } = useAuth();
  const [filter, setFilter] = useState<'all' | 'failures' | 'successes'>('all');
  const params = new URLSearchParams();
  params.set('per_page', '100');
  if (filter !== 'all') params.set('only', filter);

  const { data, isLoading, isError } = useQuery<Resp>({
    queryKey: ['super-login', filter],
    queryFn: () => api<Resp>(`/admin/super/login-attempts?${params}`),
    enabled: !!user?.is_super,
  });

  if (loading) return null;
  if (!user?.is_super) return <Navigate to="/admin/quotes" replace />;

  return (
    <>
      <Seo title="Login attempts" description="Super-admin login attempts" path="/admin/super/login-attempts" noindex />

      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
            <Award className="h-3 w-3" /> Super-admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900">Login attempts</h1>
          <p className="mt-1 text-sm text-ink-700">
            Every Google sign-in attempt, including who, why it failed, and from where.
          </p>
        </div>
        <div className="flex gap-1 rounded-full border border-paper-100 bg-paper-0 p-1 text-xs font-semibold">
          {(['all', 'successes', 'failures'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1.5 transition ${filter === f ? 'bg-navy-900 text-paper-0' : 'text-ink-700 hover:bg-paper-50'}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-ink-700"><Loader className="h-5 w-5" /> Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700">Couldn&rsquo;t load.</div>
      ) : data.entries.length === 0 ? (
        <p className="rounded-md border border-paper-100 bg-paper-50 p-4 text-sm text-ink-700">
          No matching login attempts.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-paper-100 bg-paper-0 shadow-card">
          <table className="min-w-full divide-y divide-paper-100 text-sm">
            <thead className="bg-paper-50 text-left text-xs uppercase tracking-wider text-ink-700/80">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Result</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-paper-100">
              {data.entries.map((a) => (
                <tr key={a.id} className={a.success ? 'hover:bg-paper-50' : 'bg-red-50/40 hover:bg-red-50/60'}>
                  <td className="whitespace-nowrap px-4 py-3 text-ink-700">{new Date(a.created_at).toLocaleString('en-GB')}</td>
                  <td className="px-4 py-3">
                    {a.success ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                        <Check className="h-3 w-3" /> success
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                        <Warning className="h-3 w-3" /> blocked
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-navy-900">{a.email ?? '—'}</td>
                  <td className="px-4 py-3 text-ink-700">{a.reason ?? '—'}</td>
                  <td className="px-4 py-3 text-ink-700">{a.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
